import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authProvider } from '@/authProvider.ts';
import { Button } from '@/components/ui/button';
import { useLogin, useNotify, useTranslate } from 'ra-core';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { PasswordInput } from '@/components/ui/password-input.tsx';
import { useMutation } from '@tanstack/react-query';

type Props = {
  oobCode: string;
};

const ResetPasswordPage = ({ oobCode }: Props) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState<string | null>(null);
  const notify = useNotify();
  const t = useTranslate();
  const login = useLogin();
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (newPassword !== confirmPassword) {
        notify('auth.error.password_mismatch', { type: 'error' });
        return;
      }
      await authProvider.confirmPasswordReset(oobCode, newPassword);
      notify('auth.success.password_reset', { type: 'success' });
      await login({
        email,
        password: newPassword,
        provider: 'emailAndPassword',
        isSignup: false,
      });
    },
    onError: (error: any) => {
      console.error(error);
      notify('auth.error.reset_password_failed', { type: 'error' });
    },
  });

  useEffect(() => {
    authProvider
      .verifyPasswordResetCode(oobCode)
      .then(email => setEmail(email))
      .catch(err => {
        console.error('invalid code: ', err);
        setEmail('');
      });
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    mutate();
  };

  if (email === '') {
    return (
      <div>
        <div className="mb-2">{t('auth.error.invalid_link')}</div>
        <Link to="/forgot-password">{t('auth.button.retry')}</Link>
      </div>
    );
  }

  return (
    <>
      <Link to="/">
        <img
          alt="logo"
          src="https://storage.googleapis.com/public-goloot-production/goloot/goloot-logo-white.png"
          style={{ height: 55, width: 'auto', margin: 'auto' }}
        />
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('auth.message.reset_password.set_new_password')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <PasswordInput
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder={t('auth.label.new_password')}
              autoComplete="username new-password"
            />
            <PasswordInput
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder={t('auth.label.confirm_password')}
              autoComplete="username new-password"
            />
            <Button type="submit" className="mt-2" disabled={isPending}>
              {t('auth.button.create_password')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );
};

export default ResetPasswordPage;
